/**
 * TemplatePreviewDialog
 *
 * Full-width dialog for previewing a template with optional field values.
 * Fetches the template by ID and renders it using TemplatePreviewPanel.
 */
import { Download, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTemplateRetrieveQuery } from '@/services/api/generated/templatesV2Service';

import { TemplatePreviewPanel } from '../TemplatePreviewPanel';
import { downloadTemplate } from '../utils/downloadTemplate';

interface TemplatePreviewDialogProps {
  templateId: string;
  open: boolean;
  onClose: () => void;
  fieldValues?: Record<string, string>;
}

export const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = ({
  templateId,
  open,
  onClose,
}) => {
  const { data: template, isLoading } = useTemplateRetrieveQuery(
    { id: templateId },
    { skip: !open },
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <div>
              <DialogTitle className="text-lg font-semibold">
                {isLoading
                  ? 'Loading...'
                  : (template?.display_name ?? template?.name ?? 'Template Preview')}
              </DialogTitle>
              {template && (
                <DialogDescription className="mt-1 text-sm font-medium text-zinc-500">
                  Version {template.version} &middot; {template.format}
                </DialogDescription>
              )}
            </div>
            {template?.file_url && (
              <button
                type="button"
                onClick={() => downloadTemplate(template.id as string, template.display_name || template.name)}
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="mt-2">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-zinc-400" />
              <p className="text-sm font-medium text-zinc-500">
                Loading template...
              </p>
            </div>
          )}
          {template && <TemplatePreviewPanel template={template} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
